import { ADMIN_V2_TOOL_ACCESS_REQUIRED_CODE } from '@/admin-v2/tools/access-policy';
import {
  type AdminV2ToolCreditEvidence,
  calculateAdminV2ToolCreditEvidence,
} from '@/admin-v2/tools/credit-calculator';
import type { AdminV2MediaMetaContext } from '@/admin-v2/tools/media-meta-context';
import {
  type EnhancerImageDimensionLimits,
  isEnhancerImageDimensionsTooLarge,
} from '@/ai/image/client/upload-input-file';
import { AI_IMAGE_USER_ERROR_CODES } from '@/ai/image/errors';
import {
  canEnhancerUploadFileReachSupportedMimeType,
  resolveEnhancerBrowserUploadAccept,
} from '@/ai/image/upload-image-preprocess-policy';
import type { ImageEnhancerStep } from '@/ai/image/workspace-types';
import type { ApiAiJob } from '@/ai/jobs/client/types';
import { AI_JOB_COMMAND_ERROR_CODES } from '@/ai/jobs/domain/job-command-error-contract';
import { AI_JOB_ERROR_CODES } from '@/ai/jobs/types/contract';
import { workspaceAccessConfig } from '@/config/workspace-access';
import type {
  InteractiveWorkspaceActionState,
  WorkspaceBatchItem,
  WorkspaceInputUploadStatus,
  WorkspaceJobSummary,
  WorkspaceReferenceImage,
  WorkspaceReferenceMedia,
  WorkspaceRuntimeState,
} from '../contracts/runtime-state';
import type {
  WorkspaceCreditCalculationTool,
  WorkspaceToolParamValue,
  WorkspaceToolView,
  WorkspaceToolViewKind,
} from '../contracts/tool-view';
import type { WorkspaceRuntimeCopy } from '../copy';

import {
  WorkspaceJobClientRequestError,
  isWorkspaceLoginRequiredError,
} from './job-client';
import { requiresWorkspaceStorageInputUpload } from './workspace-storage-input-owner';

export const WORKSPACE_UPLOAD_FOLDER = 'ai/input/cms-workspace';
export const WORKSPACE_JOB_ENQUEUE_FAILED_CODE = 'JOB_ENQUEUE_FAILED';
export const WORKSPACE_HISTORY_IMPORT_MAX_ATTEMPTS = 3;
const WORKSPACE_INSUFFICIENT_CREDITS_CODE = 'INSUFFICIENT_CREDITS';
export const WORKSPACE_CONCURRENCY_LIMIT_REACHED_CODE =
  'AI_JOB_CONCURRENCY_LIMIT_REACHED';
export {
  WorkspaceUploadRejectedFileError,
  resolveWorkspaceUploadFailureMessage,
} from './workspace-upload-error-presenter';

export async function resolveWorkspaceStorageInput(params: {
  file: File | null;
  inputUrl: string | null;
  resolveStorageInputOwnership: (params: {
    file: File;
    inputUrl: string;
    userId: string | null;
  }) => Promise<{ file: File; inputUrl: string }>;
  userId: string | null;
}) {
  if (
    !requiresWorkspaceStorageInputUpload({
      inputUrl: params.inputUrl,
      localFile: params.file,
      userId: params.userId,
    }) ||
    !params.file
  ) {
    return {
      file: params.file,
      inputUrl: params.inputUrl,
      uploaded: false,
    };
  }

  const upload = await params.resolveStorageInputOwnership({
    file: params.file,
    inputUrl: params.inputUrl ?? '',
    userId: params.userId,
  });

  return {
    file: upload.file,
    inputUrl: upload.inputUrl,
    uploaded: true,
  };
}

type WorkspaceConcurrencyLimitDetails = {
  paidAccess?: unknown;
};

export function resolveWorkspaceActivePromptText(
  workspaceState: Pick<
    InteractiveWorkspaceActionState,
    'activeTool' | 'primaryPrompt' | 'composerPrompt'
  >
) {
  return workspaceState.activeTool.viewKind === 'reference-composer'
    ? workspaceState.composerPrompt
    : workspaceState.primaryPrompt;
}

export function isWorkspacePromptWithinLimit(params: {
  tool: Pick<WorkspaceToolView, 'runtimeCaps'>;
  prompt: string;
}) {
  const maxPromptCharacters = params.tool.runtimeCaps.maxPromptCharacters ?? 0;
  return (
    maxPromptCharacters <= 0 || params.prompt.length <= maxPromptCharacters
  );
}

export function resolveWorkspaceActiveImageDimensionLimits(params: {
  tool: {
    runtimeCaps: Pick<
      WorkspaceToolView['runtimeCaps'],
      | 'maxImageWidth'
      | 'maxImageHeight'
      | 'maxImageMegapixels'
      | 'imageInputConstraintRules'
    >;
  };
  toolParams: Record<string, WorkspaceToolParamValue>;
}): EnhancerImageDimensionLimits | undefined {
  const activeRules = (
    params.tool.runtimeCaps.imageInputConstraintRules ?? []
  ).filter((rule) =>
    rule.activeWhen.values.some((value) =>
      Object.is(value, params.toolParams[rule.activeWhen.fieldId])
    )
  );
  const minDefined = (values: Array<number | undefined>) => {
    const defined = values.filter(
      (value): value is number => value !== undefined
    );
    return defined.length > 0 ? Math.min(...defined) : undefined;
  };

  const limits = {
    maxWidth: minDefined([
      params.tool.runtimeCaps.maxImageWidth,
      ...activeRules.map((rule) => rule.maxImageWidth),
    ]),
    maxHeight: minDefined([
      params.tool.runtimeCaps.maxImageHeight,
      ...activeRules.map((rule) => rule.maxImageHeight),
    ]),
    maxMegapixels: minDefined([
      params.tool.runtimeCaps.maxImageMegapixels,
      ...activeRules.map((rule) => rule.maxImageMegapixels),
    ]),
  };
  return Object.values(limits).some((limit) => limit !== undefined)
    ? limits
    : undefined;
}

export function hasWorkspaceImageDimensionViolation(params: {
  limits?: EnhancerImageDimensionLimits;
  images: Array<{ width: number; height: number } | null | undefined>;
}) {
  const limits = params.limits;
  return (
    limits !== undefined &&
    params.images.some(
      (image) => image && isEnhancerImageDimensionsTooLarge(image, limits)
    )
  );
}

export function resolveWorkspaceInputUploadStatus(params: {
  inputUrl: string | null;
  uploadFailed?: boolean;
}): WorkspaceInputUploadStatus {
  if (params.inputUrl) {
    return 'uploaded';
  }

  return params.uploadFailed ? 'failed' : 'uploading';
}

export type WorkspaceAccessUpgradeReason = 'paid-tool' | 'paid-setting';

export class WorkspaceTerminalJobFailureError extends Error {
  readonly job: ApiAiJob;

  constructor(job: ApiAiJob) {
    super(`Workspace job reached a failed terminal state: ${job.id}`);
    this.name = 'WorkspaceTerminalJobFailureError';
    this.job = job;
  }
}

type WorkspaceAccessViolationDetails = {
  requiredAccess: 'paid' | 'plan';
  scope: 'tool' | 'field' | 'option' | 'branch';
};

function readWorkspaceAccessViolationDetails(
  details: unknown
): WorkspaceAccessViolationDetails | null {
  let candidate = details;
  if (typeof candidate === 'string') {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return null;
    }
  }

  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    return null;
  }

  const { requiredAccess, scope } = candidate as Record<string, unknown>;
  if (requiredAccess !== 'paid' && requiredAccess !== 'plan') {
    return null;
  }
  if (
    scope !== 'tool' &&
    scope !== 'field' &&
    scope !== 'option' &&
    scope !== 'branch'
  ) {
    return null;
  }

  return { requiredAccess, scope };
}

export function resolveWorkspaceAccessUpgradeReason(
  error: unknown
): WorkspaceAccessUpgradeReason | null {
  if (
    !(error instanceof WorkspaceJobClientRequestError) ||
    error.code !== ADMIN_V2_TOOL_ACCESS_REQUIRED_CODE
  ) {
    return null;
  }

  const violation = readWorkspaceAccessViolationDetails(error.details);
  if (!violation) {
    return null;
  }

  return violation.scope === 'tool' ? 'paid-tool' : 'paid-setting';
}

export function resolveWorkspaceAcceptedUploadTypes(accept: string) {
  return accept
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

export function resolveWorkspaceBrowserUploadAccept(accept: string) {
  return resolveEnhancerBrowserUploadAccept(
    resolveWorkspaceAcceptedUploadTypes(accept)
  );
}

export function isWorkspaceUploadFileTypeAccepted(
  file: Pick<File, 'name' | 'type'>,
  accept: string
) {
  return canEnhancerUploadFileReachSupportedMimeType(
    file,
    resolveWorkspaceAcceptedUploadTypes(accept)
  );
}

export function isWorkspaceCreditBlockedError(code: string) {
  return (
    code === WORKSPACE_INSUFFICIENT_CREDITS_CODE ||
    code ===
      AI_JOB_COMMAND_ERROR_CODES.DEVICE_DAILY_FREE_CREDITS_ALREADY_CLAIMED
  );
}

export function isWorkspaceConcurrencyLimitError(code: string) {
  return code === WORKSPACE_CONCURRENCY_LIMIT_REACHED_CODE;
}

function readWorkspaceConcurrencyLimitDetails(
  error: unknown
): WorkspaceConcurrencyLimitDetails | null {
  if (!(error instanceof WorkspaceJobClientRequestError)) {
    return null;
  }

  if (!isWorkspaceConcurrencyLimitError(error.code ?? '')) {
    return null;
  }

  if (!error.details || typeof error.details !== 'object') {
    return null;
  }

  return error.details as WorkspaceConcurrencyLimitDetails;
}

export function shouldOpenWorkspaceConcurrencyLimitUpgrade(error: unknown) {
  if (!workspaceAccessConfig.paidEntitlementsEnabled) return false;
  const details = readWorkspaceConcurrencyLimitDetails(error);
  return details !== null && details.paidAccess !== true;
}

function resolveWorkspaceConcurrencyLimitMessage(
  error: unknown,
  copy: WorkspaceRuntimeCopy
) {
  const details = readWorkspaceConcurrencyLimitDetails(error);
  if (details?.paidAccess === true) {
    return copy.paidConcurrencyLimitMessage;
  }

  return copy.freeConcurrencyLimitMessage;
}

export function resolveWorkspaceJobErrorCode(job: ApiAiJob): string {
  return job.error?.code ?? 'REQUEST_FAILED';
}

export function resolveWorkspaceJobErrorMessage(
  job: ApiAiJob,
  copy: WorkspaceRuntimeCopy
): string {
  return resolveWorkspaceGenerationErrorMessage(
    resolveWorkspaceJobErrorCode(job),
    copy,
    job.creditOutcome
  );
}

function resolveWorkspaceErrorMessage(
  code: string,
  copy: WorkspaceRuntimeCopy
) {
  switch (code) {
    case 'TIMEOUT':
      return copy.resultTimeout;
    case AI_IMAGE_USER_ERROR_CODES.INPUT_IMAGE_DIMENSIONS_TOO_SMALL:
      return copy.inputImageDimensionsTooSmall;
    case AI_IMAGE_USER_ERROR_CODES.UPSCALE_SOURCE_TOO_SMALL_FOR_TARGET:
      return copy.upscaleSourceTooSmallForTarget;
    case 'INSUFFICIENT_CREDITS':
      return copy.generationFailed;
    case WORKSPACE_CONCURRENCY_LIMIT_REACHED_CODE:
      return copy.freeConcurrencyLimitMessage;
    default:
      return copy.generationFailed;
  }
}

function appendWorkspaceFailurePaymentOutcome(
  message: string,
  copy: Pick<
    WorkspaceRuntimeCopy,
    'failureRefunded' | 'failurePaidRetryAvailable'
  >,
  creditOutcome?: ApiAiJob['creditOutcome']
) {
  if (creditOutcome === 'released') {
    return `${message} ${copy.failureRefunded}`;
  }
  if (creditOutcome === 'charged') {
    return `${message} ${copy.failurePaidRetryAvailable}`;
  }
  return message;
}

export function resolveWorkspaceGenerationErrorMessage(
  code: string,
  copy: WorkspaceRuntimeCopy,
  creditOutcome?: ApiAiJob['creditOutcome']
) {
  const message =
    code === AI_JOB_ERROR_CODES.CONTENT_POLICY_BLOCKED
      ? copy.contentPolicyBlocked
      : resolveWorkspaceErrorMessage(code, copy);
  return appendWorkspaceFailurePaymentOutcome(message, copy, creditOutcome);
}

type WorkspacePersistedJobFailureCopy = Pick<
  WorkspaceRuntimeCopy,
  | 'contentPolicyBlocked'
  | 'failureRefunded'
  | 'failurePaidRetryAvailable'
  | 'generationFailed'
>;

export function resolveWorkspacePersistedJobFailurePresentation(
  code: string,
  copy: WorkspacePersistedJobFailureCopy,
  creditOutcome?: ApiAiJob['creditOutcome']
) {
  const contentPolicyBlocked =
    code === AI_JOB_ERROR_CODES.CONTENT_POLICY_BLOCKED;
  return {
    kind: contentPolicyBlocked ? ('rejected' as const) : ('failed' as const),
    message: appendWorkspaceFailurePaymentOutcome(
      contentPolicyBlocked ? copy.contentPolicyBlocked : copy.generationFailed,
      copy,
      creditOutcome
    ),
  };
}

export function resolveWorkspaceThrowableErrorCode(error: unknown): string {
  if (error instanceof WorkspaceTerminalJobFailureError) {
    return resolveWorkspaceJobErrorCode(error.job);
  }

  if (error instanceof WorkspaceJobClientRequestError && error.code) {
    return error.code;
  }

  if (error instanceof Error && error.message === 'TIMEOUT') {
    return 'TIMEOUT';
  }

  return 'REQUEST_FAILED';
}

export function resolveWorkspaceThrowableErrorMessage(
  error: unknown,
  copy: WorkspaceRuntimeCopy
): string {
  if (isWorkspaceLoginRequiredError(error)) {
    return copy.genericError;
  }

  return copy.genericError;
}

export function resolveWorkspaceGenerationThrowableErrorMessage(
  error: unknown,
  copy: WorkspaceRuntimeCopy
): string {
  if (error instanceof WorkspaceTerminalJobFailureError) {
    return resolveWorkspaceJobErrorMessage(error.job, copy);
  }

  if (isWorkspaceLoginRequiredError(error)) {
    return copy.genericError;
  }

  const errorCode = resolveWorkspaceThrowableErrorCode(error);
  if (isWorkspaceConcurrencyLimitError(errorCode)) {
    return resolveWorkspaceConcurrencyLimitMessage(error, copy);
  }

  if (
    errorCode === AI_IMAGE_USER_ERROR_CODES.INPUT_IMAGE_NOT_OWNED ||
    errorCode === AI_IMAGE_USER_ERROR_CODES.INPUT_IMAGE_NOT_FOUND
  ) {
    return copy.missingHistorySourceImage;
  }

  return resolveWorkspaceGenerationErrorMessage(errorCode, copy);
}

export function resolveRuntimeStateFromRemainingBatchItem(
  item: WorkspaceBatchItem
) {
  switch (item.status) {
    case 'processing':
      return 'processing';
    case 'succeeded':
      return item.resultImageUrl ? 'done' : 'idle';
    case 'failed':
      return 'failed';
    case 'queued':
      return 'idle';
  }
}

export function isWorkspaceAbortError(error: unknown) {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

export function sleepWorkspace(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export function waitForWorkspacePreviewFrame() {
  return new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => resolve());
    });
  });
}

export function resolveWorkspaceRuntimeStateFromJob(
  job: WorkspaceJobSummary | ApiAiJob
): WorkspaceRuntimeState {
  if (job.status === 'succeeded') {
    return 'done';
  }

  if (job.status === 'failed') {
    return 'failed';
  }

  if (job.stage === 'queued') {
    return 'queued';
  }

  return 'processing';
}

export function buildWorkspaceJobInput(params: {
  tool: {
    fields: ReadonlyArray<{ id: string }>;
    prefillFields?: ReadonlyArray<{ id: string }>;
  };
  viewKind: WorkspaceToolViewKind;
  inputBindings: NonNullable<WorkspaceToolView['inputBindings']>;
  toolParams: Record<string, WorkspaceToolParamValue>;
  inputImageUrl?: string | null;
  prompt?: string;
  referenceImageUrlsByFieldId?: Record<string, string[]>;
  referenceMediaUrlsByFieldId?: Record<string, string[]>;
  explicitMediaFieldValues?: Record<string, string | string[]>;
}) {
  const clientFieldIds = new Set(
    [...params.tool.fields, ...(params.tool.prefillFields ?? [])].map(
      (field) => field.id
    )
  );
  const input: Record<string, unknown> = Object.fromEntries(
    Object.entries(params.toolParams).filter(([fieldId]) =>
      clientFieldIds.has(fieldId)
    )
  );

  for (const group of params.inputBindings.templatePickerGroups ?? []) {
    const officialValue = input[group.selectionFieldId];
    const hasOfficialValue =
      typeof officialValue === 'string' && officialValue.trim().length > 0;
    if (hasOfficialValue) {
      if (group.customImageFieldId) {
        delete input[group.customImageFieldId];
      }
      continue;
    }

    delete input[group.selectionFieldId];
    if (group.customImageFieldId) {
      const customValue = input[group.customImageFieldId];
      if (typeof customValue === 'string' && customValue.trim().length === 0) {
        delete input[group.customImageFieldId];
      }
    }
  }

  if (params.inputBindings.primaryImageFieldId && params.inputImageUrl) {
    input[params.inputBindings.primaryImageFieldId] = params.inputImageUrl;
  }

  if (params.inputBindings.promptFieldId) {
    const prompt = params.prompt?.trim() ?? '';
    if (prompt) {
      input[params.inputBindings.promptFieldId] = prompt;
    }
  }

  for (const fieldId of [
    ...params.inputBindings.referenceImageFieldIds,
    ...(params.inputBindings.firstFrameFieldId
      ? [params.inputBindings.firstFrameFieldId]
      : []),
    ...(params.inputBindings.lastFrameFieldId
      ? [params.inputBindings.lastFrameFieldId]
      : []),
  ]) {
    const referenceUrls = params.referenceImageUrlsByFieldId?.[fieldId] ?? [];
    if (referenceUrls.length === 1) {
      input[fieldId] = referenceUrls[0];
    } else if (referenceUrls.length > 1) {
      input[fieldId] = referenceUrls;
    }
  }

  for (const fieldId of [
    ...(params.inputBindings.referenceVideoFieldIds ?? []),
    ...(params.inputBindings.referenceAudioFieldIds ?? []),
  ]) {
    const urls = params.referenceMediaUrlsByFieldId?.[fieldId] ?? [];
    if (urls.length === 1) input[fieldId] = urls[0];
    else if (urls.length > 1) input[fieldId] = urls;
  }

  for (const [fieldId, value] of Object.entries(
    params.explicitMediaFieldValues ?? {}
  )) {
    if (clientFieldIds.has(fieldId)) input[fieldId] = value;
  }

  return input;
}

export function buildWorkspaceReferenceImageUrlsByFieldId(
  referenceImages: WorkspaceReferenceImage[]
) {
  return referenceImages.reduce<Record<string, string[]>>(
    (byFieldId, image) => {
      if (!image.inputUrl) {
        return byFieldId;
      }

      byFieldId[image.fieldId] = [
        ...(byFieldId[image.fieldId] ?? []),
        image.inputUrl,
      ];
      return byFieldId;
    },
    {}
  );
}

export function buildWorkspaceReferenceMediaUrlsByFieldId(
  referenceMedia: WorkspaceReferenceMedia[]
) {
  return referenceMedia.reduce<Record<string, string[]>>((byFieldId, item) => {
    if (!item.inputUrl) return byFieldId;
    byFieldId[item.fieldId] = [
      ...(byFieldId[item.fieldId] ?? []),
      item.inputUrl,
    ];
    return byFieldId;
  }, {});
}

export function buildWorkspaceClientMediaMetaRecord(
  referenceMedia: WorkspaceReferenceMedia[]
) {
  return referenceMedia.reduce<Record<string, unknown>>((byFieldId, item) => {
    if (!item.inputUrl || !item.clientMeta) return byFieldId;
    const existing = byFieldId[item.fieldId];
    if (existing === undefined) byFieldId[item.fieldId] = item.clientMeta;
    else if (Array.isArray(existing))
      byFieldId[item.fieldId] = [...existing, item.clientMeta];
    else byFieldId[item.fieldId] = [existing, item.clientMeta];
    return byFieldId;
  }, {});
}

export function calculateWorkspaceCreditEvidence(params: {
  tool: WorkspaceCreditCalculationTool;
  toolParams: Record<string, WorkspaceToolParamValue>;
  hasPaidAccess?: boolean;
  inputImageUrl?: string | null;
  prompt?: string;
  referenceImageUrlsByFieldId?: Record<string, string[]>;
  clientMediaMeta?: AdminV2MediaMetaContext;
}) {
  if (!params.tool.inputBindings) {
    throw new Error(
      `Workspace Tool input bindings are missing: ${params.tool.toolId}`
    );
  }

  const input = buildWorkspaceJobInput({
    tool: params.tool,
    viewKind: params.tool.viewKind,
    inputBindings: params.tool.inputBindings,
    toolParams: params.toolParams,
    inputImageUrl: params.inputImageUrl,
    prompt: params.prompt,
    referenceImageUrlsByFieldId: params.referenceImageUrlsByFieldId,
  });

  return calculateAdminV2ToolCreditEvidence({
    policy: params.tool.creditPolicy,
    input,
    hasPaidAccess: params.hasPaidAccess,
    mediaMetaContext: params.clientMediaMeta,
  });
}

export function calculateWorkspaceRequiredCredits(
  params: Parameters<typeof calculateWorkspaceCreditEvidence>[0]
) {
  return calculateWorkspaceCreditEvidence(params).totalCredits;
}

export function toWorkspaceCreditDiscountQuote(
  evidence: AdminV2ToolCreditEvidence | null,
  params: {
    percentOff?: number;
    applied: boolean;
  }
) {
  const discount = evidence?.paidAccessDiscount;
  const percentOff = discount?.percentOff ?? params.percentOff;
  if (percentOff === undefined) {
    return null;
  }

  return {
    percentOff,
    applied: discount?.applied ?? params.applied,
    creditsBeforeDiscount: discount?.creditsBeforeDiscount ?? null,
  };
}

export function getWorkspaceClientCreditMetaFieldIds(
  tool: Pick<WorkspaceToolView, 'creditPolicy'>
) {
  return Array.from(
    new Set(
      tool.creditPolicy.terms.flatMap((term) =>
        term.factors.flatMap((factor) =>
          factor.kind === 'conditional'
            ? factor.conditions.flatMap((condition) =>
                condition.source === 'clientImageMeta'
                  ? [condition.fieldId]
                  : []
              )
            : []
        )
      )
    )
  );
}

export function resolveWorkspaceRequiredCredits(params: {
  tool: WorkspaceCreditCalculationTool;
  toolParams: Record<string, WorkspaceToolParamValue>;
  hasPaidAccess?: boolean;
  inputImageUrl?: string | null;
  prompt?: string;
  referenceImageUrlsByFieldId?: Record<string, string[]>;
  quotedCredits: number | null;
}) {
  if (params.tool.runtimeCaps.creditCalculationMode === 'server_quote') {
    return params.quotedCredits;
  }
  if (params.quotedCredits !== null) {
    return params.quotedCredits;
  }
  if (getWorkspaceClientCreditMetaFieldIds(params.tool).length > 0) {
    return null;
  }
  return calculateWorkspaceRequiredCredits(params);
}

export function resolveWorkspaceBatchRequiredCredits(params: {
  tool: WorkspaceCreditCalculationTool;
  toolParams: Record<string, WorkspaceToolParamValue>;
  hasPaidAccess?: boolean;
  items: Array<{ inputImageUrl: string | null }>;
  prompt?: string;
  quotedCredits: number | null;
}) {
  if (params.tool.runtimeCaps.creditCalculationMode === 'server_quote') {
    return params.quotedCredits;
  }
  if (params.quotedCredits !== null) {
    return params.quotedCredits;
  }
  if (getWorkspaceClientCreditMetaFieldIds(params.tool).length > 0) {
    return null;
  }
  return params.items.reduce(
    (total, item) =>
      total +
      calculateWorkspaceRequiredCredits({
        tool: params.tool,
        toolParams: params.toolParams,
        hasPaidAccess: params.hasPaidAccess,
        inputImageUrl: item.inputImageUrl,
        prompt: params.prompt,
      }),
    0
  );
}

export function resolveWorkspaceCanvasStep(params: {
  runtimeState: InteractiveWorkspaceActionState['state'];
  inputUploadStatus: WorkspaceInputUploadStatus | null;
}): ImageEnhancerStep {
  if (params.runtimeState === 'uploading') {
    return params.inputUploadStatus === 'uploading' ? 'uploading' : 'idle';
  }

  switch (params.runtimeState) {
    case 'submitting':
    case 'queued':
    case 'processing':
      return params.runtimeState;
    default:
      return 'idle';
  }
}
